// app/location-map.tsx
import { router, useLocalSearchParams } from "expo-router";
import { ArrowLeft, Map as MapIcon, MapPin, Share2 } from "lucide-react-native";
import React, { useMemo } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  Share,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { WebView } from "react-native-webview";

function parseNumber(value: string | string[] | undefined) {
  const raw = Array.isArray(value) ? value[0] : value;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function readParam(value: string | string[] | undefined) {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === "string" && raw.trim() ? raw.trim() : "";
}

function buildGoogleMapsPinUrl(query: string) {
  // Opens a clean Google Maps pin/search result.
  // This is better than forcing directions immediately.
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
}

export default function LocationMapScreen() {
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();

  const lat = parseNumber(params.lat);
  const lng = parseNumber(params.lng);

  // Business name first.
  // Do not use road, town, building, or address as the business name.
  const title =
    readParam(params.businessName) ||
    readParam(params.title) ||
    readParam(params.name) ||
    "Business location";

  const subtitle = readParam(params.subtitle);

  // Customer-facing address.
  // Expected example:
  // Banana Rd, Rauka, Henry Garage, Kikuyu Town, Kikuyu
  const addressLine =
    readParam(params.addressLine) ||
    readParam(params.address) ||
    readParam(params.formattedAddress);

  const cityLine = readParam(params.cityLine);
  const queryParam = readParam(params.query);

  const mapQuery = useMemo(() => {
    // Coordinates are the most accurate.
    if (lat != null && lng != null) {
      return `${lat},${lng}`;
    }

    // Fallback if coordinates are missing.
    return queryParam || [addressLine, cityLine, title].filter(Boolean).join(", ");
  }, [lat, lng, queryParam, addressLine, cityLine, title]);

  const mapPinUrl = useMemo(() => {
    if (!mapQuery) return "";
    return buildGoogleMapsPinUrl(mapQuery);
  }, [mapQuery]);

  async function openInGoogleMapsApp() {
    if (!mapPinUrl) return;

    try {
      await Linking.openURL(mapPinUrl);
    } catch {}
  }

  async function shareLocation() {
    if (!mapPinUrl) return;

    try {
      await Share.share({
        title,
        message: `${title}\n${addressLine || subtitle || ""}\n${mapPinUrl}`,
      });
    } catch {}
  }

  if (!mapPinUrl) {
    return (
      <View style={styles.safeArea}>
        <View
          style={[
            styles.header,
            {
              paddingTop: insets.top + 12,
            },
          ]}
        >
          <View style={styles.headerTopRow}>
            <Pressable style={styles.backButton} onPress={() => router.back()}>
              <ArrowLeft size={28} color="#0f172a" strokeWidth={2.5} />
              <Text style={styles.backText}>Back</Text>
            </Pressable>
          </View>
        </View>

        <View style={styles.emptyWrap}>
          <MapPin size={42} color="#94a3b8" strokeWidth={2.2} />
          <Text style={styles.emptyTitle}>No location found</Text>
          <Text style={styles.emptyText}>
            This business does not have a valid map location yet.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.safeArea}>
      <View
        style={[
          styles.header,
          {
            paddingTop: insets.top + 12,
          },
        ]}
      >
        <View style={styles.headerTopRow}>
          <Pressable style={styles.backButton} onPress={() => router.back()}>
            <ArrowLeft size={28} color="#0f172a" strokeWidth={2.5} />
            <Text style={styles.backText}>Back</Text>
          </Pressable>

          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle} numberOfLines={1}>
              {title}
            </Text>

            {!!addressLine && (
              <Text style={styles.headerSubtitle} numberOfLines={1}>
                {addressLine}
              </Text>
            )}

            {!addressLine && !!subtitle && (
              <Text style={styles.headerSubtitle} numberOfLines={1}>
                {subtitle}
              </Text>
            )}
          </View>

          <Pressable style={styles.shareButton} onPress={shareLocation}>
            <Share2 size={20} color="#0f172a" strokeWidth={2.5} />
          </Pressable>
        </View>
      </View>

      <View style={styles.mapWrap}>
        <WebView
          key={mapPinUrl}
          source={{ uri: mapPinUrl }}
          style={styles.webView}
          originWhitelist={["*"]}
          javaScriptEnabled
          domStorageEnabled
          geolocationEnabled
          startInLoadingState
          setSupportMultipleWindows={false}
          renderLoading={() => (
            <View style={styles.loadingWrap}>
              <ActivityIndicator size="large" color="#0f766e" />
              <Text style={styles.loadingText}>Loading Google Maps...</Text>
            </View>
          )}
          onShouldStartLoadWithRequest={(request) => {
            const url = request.url;

            // Keep normal Google Maps web pages inside the in-app map screen.
            if (
              url.startsWith("https://") ||
              url.startsWith("http://") ||
              url === "about:blank"
            ) {
              return true;
            }

            // If Google Maps tries to open app-specific links,
            // allow the phone to handle them externally.
            Linking.openURL(url).catch(() => undefined);
            return false;
          }}
        />

        <Pressable
          style={[
            styles.openAppButton,
            {
              bottom: insets.bottom + 24,
            },
          ]}
          onPress={openInGoogleMapsApp}
        >
          <MapIcon size={18} color="#ffffff" strokeWidth={2.5} />
          <Text style={styles.openAppButtonText}>Open in Google Maps</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#ffffff",
  },

  header: {
    paddingHorizontal: 14,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    backgroundColor: "#ffffff",
  },

  headerTopRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingVertical: 8,
    paddingRight: 10,
    flexShrink: 0,
  },

  backText: {
    fontSize: 18,
    fontWeight: "900",
    color: "#0f172a",
  },

  headerCenter: {
    flex: 1,
    minWidth: 0,
    paddingLeft: 4,
  },

  headerTitle: {
    fontSize: 17,
    fontWeight: "900",
    color: "#0f172a",
  },

  headerSubtitle: {
    marginTop: 3,
    fontSize: 12,
    fontWeight: "700",
    color: "#64748b",
  },

  shareButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    marginLeft: 8,
    flexShrink: 0,
  },

  mapWrap: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },

  webView: {
    flex: 1,
    backgroundColor: "#f8fafc",
  },

  loadingWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    gap: 10,
  },

  loadingText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#475569",
  },

  openAppButton: {
    position: "absolute",
    left: 16,
    right: 16,
    height: 52,
    borderRadius: 18,
    backgroundColor: "#0f766e",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    elevation: 5,
    shadowColor: "#000",
    shadowOpacity: 0.16,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },

  openAppButtonText: {
    color: "#ffffff",
    fontSize: 15,
    fontWeight: "900",
  },

  emptyWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 10,
  },

  emptyTitle: {
    fontSize: 22,
    fontWeight: "900",
    color: "#0f172a",
  },

  emptyText: {
    fontSize: 15,
    color: "#64748b",
    textAlign: "center",
  },
});
